// 玩法 Demo 場景庫——兩套旅程（郵寄＋到店）逐幕定義
// 設計照 couponly /demo 哲學：圖畫表述思想，文字只是輔助（每幕一句 caption）
// 全部假數據零後端；色調 X-1493 紅 #d9381e
import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Chat, QrImg } from './GfStoryPhone';

export interface GfScene {
  icon: string;
  node: string;        // FlowMap 節點標籤（2-4 字）
  caption: string;     // 一句 caption（文字輔助）
  phoneLabel: string;  // 手機框下標籤
  phone: ReactNode;    // 手機畫面內容
}

const RED = '#d9381e';
const LINK = 'https://hkspicyvalley.com/gf/98DD';

// ── 共用畫面元件 ──

/** 落地頁表單（打字 caret） */
function FormScreen() {
  return (
    <div className="flex h-full flex-col bg-white px-4 pt-8">
      <div className="mb-3 text-center">
        <p className="text-[10px] font-bold" style={{ color: RED }}>🎁 文記辣椒油｜谷友體驗福利</p>
        <p className="text-[9px] text-gray-400">免費試吃裝 到店自選兌換</p>
      </div>
      <div className="space-y-2.5">
        <div className="rounded-lg border border-gray-300 px-3 py-2 text-[11px] text-gray-700">陳大文<span className="type-caret">▌</span></div>
        <div className="rounded-lg border border-gray-300 px-3 py-2 text-[11px] text-gray-700">九龍旺角彌敦道 123 號</div>
        <div className="rounded-lg border border-gray-300 px-3 py-2 text-[11px] text-gray-500 flex justify-between items-center">
          <span>🏪 華星冰室（尖沙咀）</span><span className="text-[9px]">▾</span>
        </div>
        <div className="rounded-lg py-2.5 text-center text-[11px] font-bold text-white" style={{ background: RED }}>立即領取 🌶️</div>
      </div>
    </div>
  );
}

/** WhatsApp deeplink 驗證畫面 */
function VerifyScreen() {
  return (
    <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
      <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
      <div className="flex-1 flex flex-col justify-end pb-4">
        <div className="rounded-2xl rounded-tr-sm bg-[#005c4b] px-3 py-2.5 text-[11px] leading-relaxed text-white ml-auto max-w-[85%]">
          香港辣谷禮品驗證 <b>2G6SHW</b>
        </div>
        <div className="mt-2 flex justify-center">
          <div className="rounded-full px-4 py-2 text-[10px] font-bold text-white shadow-lg" style={{ background: '#00a884' }}>
            ➤ 發送
          </div>
        </div>
      </div>
    </div>
  );
}

/** 系統確認訊息（Chat 泡逐步彈出） */
function ConfirmScreen() {
  return (
    <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
      <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
      <div className="flex-1 flex flex-col justify-end gap-2 pb-4">
        <div className="demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]">
          ✅ 登記成功！【文記辣椒油｜谷友體驗福利】<br /><br />禮品將於 3-5 個工作天內郵寄到：<br />九龍旺角彌敦道 123 號<br /><br />📸 收到後 7 日內出分享帖（tag #香港辣谷）🌶️
        </div>
        <div className="demo-pop rounded-2xl bg-[#005c4b] px-3 py-2.5 text-[10px] text-white ml-auto max-w-[60%]" style={{ animationDelay: '.8s' }}>
          你已成為辣谷之友 ✅ 專屬連結：hkspicyvalley.com/?r=N4VFSG
        </div>
      </div>
    </div>
  );
}

/** 後台卡片（admin 動手幕） */
function AdminCard({ action, label }: { action: string; label: string }) {
  return (
    <div className="flex h-full flex-col bg-[#f5f0eb] px-4 pt-8 dark:bg-[#1c1917]">
      <div className="mb-3 text-center text-[9px] font-bold text-gray-400">香港辣谷 管理後台</div>
      <div className="spot-card rounded-xl border-2 bg-white p-3 shadow-lg dark:bg-gray-800" style={{ borderColor: 'transparent' }}>
        <p className="text-[9px] font-bold text-gray-400">{label}</p>
        <p className="mt-1 text-[12px] font-bold text-gray-800 dark:text-white">🎁 文記辣椒油｜谷友體驗福利</p>
        <p className="text-[10px] text-gray-500">58 人領取 · 進行中</p>
        <div className="mt-3 rounded-lg px-3 py-2.5 text-center text-[11px] font-bold text-white" style={{ background: RED }}>
          {action}
        </div>
      </div>
    </div>
  );
}

/** 到店兌換頁（QR 大圖） */
function QrScreen({ stage }: { stage: 'customer' | 'staff' | 'done' }) {
  return (
    <div className="flex h-full flex-col bg-white px-4 pt-7">
      <div className="mb-2 rounded-lg px-3 py-2 text-center text-[10px] font-bold text-white" style={{ background: RED }}>
        🎁 谷友禮品到店兌換
      </div>
      {stage === 'customer' && (
        <>
          <div className="flex flex-col items-center py-2">
            <QrImg value={LINK} size={128} />
            <p className="mt-2 font-mono text-sm font-bold tracking-widest text-gray-800">GP6M6K</p>
            <p className="text-[9px] text-gray-400">到店出示此 QR 俾店員掃描</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-2.5 text-[10px] text-gray-600">
            🏪 華星冰室（尖沙咀）· 彌敦道 1 號<br />
            ⏰ 有效期至 10/09/2026
          </div>
        </>
      )}
      {stage === 'staff' && (
        <div className="flex-1 space-y-3">
          <p className="rounded-lg bg-gray-50 p-2 text-[10px] text-gray-600">🏪 華星冰室（尖沙咀）<br />👤 客人：陳氏 · GP6M6K</p>
          <p className="text-[10px] font-medium text-gray-500">店員核銷：輸入本店驗證碼（4 位數字）</p>
          <div className="flex gap-2">
            <div className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-center font-mono text-lg tracking-widest text-gray-800">1<span className="type-caret">▌</span></div>
          </div>
          <div className="rounded-lg py-2.5 text-center text-[11px] font-bold text-white" style={{ background: RED }}>確認領取</div>
        </div>
      )}
      {stage === 'done' && (
        <div className="flex-1 space-y-3">
          <div className="rounded-xl bg-green-50 p-3 text-center">
            <p className="text-[13px] font-black text-green-700">✓ 已領取 ✓</p>
            <p className="text-[9px] text-green-600">2026/09/29 12:30</p>
          </div>
          <div className="demo-pop rounded-xl border border-orange-200 bg-orange-50 p-3" style={{ animationDelay: '.6s' }}>
            <p className="text-[10px] font-bold text-orange-800">🎉 本店係辣谷合作夥伴——出示優惠即場享有：</p>
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-white p-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg text-lg" style={{ background: '#fdeae5' }}>🍞</div>
              <div>
                <p className="text-[10px] font-bold text-gray-800">麻辣滑蛋厚多士半價</p>
                <p className="text-[9px] text-gray-400">至 31/10/2026</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 郵寄模式 8 幕 ──
export function mailScenes(): GfScene[] {
  return [
    { icon: '📣', node: '貼連結', caption: '後台建好活動，/gf/98DD 短碼貼去 FB／IG／WhatsApp 群組，即開即收。', phoneLabel: '客人見到廣告', phone: (
      <div className="flex h-full flex-col bg-white px-4 pt-9">
        <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
          <div className="h-8 w-8 rounded-full" style={{ background: RED }}><span className="flex h-full w-full items-center justify-center text-[10px] font-bold text-white">X1</span></div>
          <div><p className="text-[10px] font-bold text-gray-800">香港辣谷 HKSpicyValley</p><p className="text-[8px] text-gray-400">Sponsored · 🌐</p></div>
        </div>
        <p className="text-[11px] font-bold text-gray-800">🎁 谷友體驗福利——免費試吃裝到店領！</p>
        <p className="mt-1 text-[9px] text-gray-500">香港 50+ 年老字號｜2026 全新獨立包裝 — 每人體驗禮：4 包獨立裝辣椒油</p>
        <div className="mt-2 flex items-center justify-center rounded-lg bg-gray-100 py-3 text-[10px] font-bold text-blue-600">{LINK.replace('https://', '')}</div>
      </div>
    ) },
    { icon: '📝', node: '填表', caption: '客人 30 秒填好姓名地址，揀門店——零門檻參與。', phoneLabel: '落地頁', phone: <FormScreen /> },
    { icon: '💬', node: '驗證', caption: '自動彈出 WhatsApp 驗證訊息，按一下發送——防亂領，號碼即學到。', phoneLabel: 'WhatsApp 驗證', phone: <VerifyScreen /> },
    { icon: '📲', node: '秒回確認', caption: '系統秒回登記確認＋任務詳情＋會員專屬連結——第一步完成。', phoneLabel: '系統自動', phone: <ConfirmScreen /> },
    { icon: '📦', node: '標記寄出', caption: '你喺領取名單一按「標記已寄出」——狀態流轉，安靜唔滋擾客人。', phoneLabel: '你要郁手', phone: <AdminCard action="標記已寄出" label="領取名單 · 郵寄狀態" /> },
    { icon: '⏰', node: 'T+2 提醒', caption: '寄出兩日後假定已收妥——系統自動 WhatsApp 溫和提醒出分享帖。', phoneLabel: '自動跟進', phone: (
      <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
        <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
        <div className="flex-1 flex flex-col justify-end gap-2 pb-4">
          <div className="demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]">
            📦 谷友，禮品到手未呀？🌶️<br /><br />希望食得合口味！食落覺得正嘅話——<br />📸 影張相＋一句真實感想<br />🏷️ Tag #香港辣谷 出個帖<br /><br />你嘅一個分享，係對本地品牌最實際嘅支持 🙏
          </div>
        </div>
      </div>
    ) },
    { icon: '🔗', node: '回貼連結', caption: '客人出分享帖，連結 WhatsApp 傳返——系統自動捕捉入任務跟蹤。', phoneLabel: '客人回貼', phone: <VerifyScreen /> },
    { icon: '🙏', node: '自動確認', caption: '連結一到，系統秒回「已記錄，核實後谷友福利就完成」——客人即時安心。', phoneLabel: '系統自動', phone: (
      <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
        <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
        <div className="flex-1 flex flex-col justify-end gap-2 pb-4">
          <div className="demo-pop rounded-2xl rounded-tr-sm bg-[#005c4b] px-3 py-2.5 text-[10px] text-white ml-auto max-w-[85%]">
            https://www.facebook.com/share/p/19by9HmFHc/
          </div>
          <div className="demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]" style={{ animationDelay: '.6s' }}>
            🙏 收到你嘅分享連結，陳大文！已記錄，核實後你嘅谷友福利就完成 ✅
          </div>
        </div>
      </div>
    ) },
    { icon: '✅', node: '核實', caption: '你一按核實——感謝訊息自動出，成效漏斗自動累積，閉環完成。', phoneLabel: '數據閉環', phone: <AdminCard action="✅ 分享已核實" label="任務跟蹤 · 待核實" /> },
  ];
}

// ── 到店模式 9 幕 ──
export function pickupScenes(): GfScene[] {
  return [
    { icon: '📣', node: '貼連結', caption: '同一套後台，發放模式揀「到店自選兌換」——短碼照貼。', phoneLabel: '客人見到廣告', phone: (
      <div className="flex h-full flex-col bg-white px-4 pt-9">
        <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
          <div className="h-8 w-8 rounded-full" style={{ background: RED }}><span className="flex h-full w-full items-center justify-center text-[10px] font-bold text-white">X1</span></div>
          <div><p className="text-[10px] font-bold text-gray-800">香港辣谷 HKSpicyValley</p><p className="text-[8px] text-gray-400">Sponsored · 🌐</p></div>
        </div>
        <p className="text-[11px] font-bold text-gray-800">🎁 谷友體驗福利——到店自選兌換！</p>
        <p className="mt-1 text-[9px] text-gray-500">揀你方便嘅門店，免費試吃裝等你嚟領</p>
        <div className="mt-2 flex items-center justify-center rounded-lg bg-gray-100 py-3 text-[10px] font-bold text-blue-600">{LINK.replace('https://', '')}</div>
      </div>
    ) },
    { icon: '🏪', node: '揀門店', caption: '客人揀店那一刻，該店招牌優惠即時彈出——到訪動機即刻鎖定。', phoneLabel: '落地頁 · 揀店', phone: (
      <div className="flex h-full flex-col bg-white px-4 pt-8">
        <div className="mb-2 text-center">
          <p className="text-[10px] font-bold" style={{ color: RED }}>🎁 文記辣椒油｜谷友體驗福利</p>
        </div>
        <div className="rounded-lg border-2 px-3 py-2 text-[11px] font-bold text-gray-700" style={{ borderColor: RED }}>
          🏪 華星冰室（尖沙咀）
        </div>
        <div className="demo-pop mt-2 rounded-xl border border-orange-200 bg-orange-50 p-2.5" style={{ animationDelay: '.5s' }}>
          <p className="text-[9px] font-bold text-orange-800">🎁 順便試埋呢間店嘅招牌優惠：</p>
          <div className="mt-1.5 flex items-center gap-2 rounded-lg bg-white p-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg text-base" style={{ background: '#fdeae5' }}>🍞</div>
            <div><p className="text-[10px] font-bold text-gray-800">麻辣滑蛋厚多士半價</p><p className="text-[9px] text-gray-400">至 31/10/2026</p></div>
          </div>
        </div>
      </div>
    ) },
    { icon: '💬', node: '驗證', caption: 'WhatsApp 一鍵驗證——號碼真實性確認，防亂領。', phoneLabel: 'WhatsApp 驗證', phone: <VerifyScreen /> },
    { icon: '📲', node: '收 QR', caption: '系統秒回：兌換碼＋10 日效期專屬 QR＋門店地址＋該店優惠。', phoneLabel: '系統自動', phone: <QrScreen stage="customer" /> },
    { icon: '🏪', node: '到店出示', caption: '10 日內到門店，開兌換頁出示大 QR——線上流量變店內客流。', phoneLabel: '客人到店', phone: <QrScreen stage="customer" /> },
    { icon: '📱', node: '店員核銷', caption: '店員掃客人 QR，輸入本店驗證碼（預設 1493，商家自訂）——錯 3 次自動鎖。', phoneLabel: '店員模式', phone: <QrScreen stage="staff" /> },
    { icon: '🎉', node: '核銷成功', caption: '核銷那一秒，本店優惠已自動放入客人賬戶——出示券即刻用，零額外步驟。', phoneLabel: '優惠即刻用得', phone: <QrScreen stage="done" /> },
    { icon: '⏰', node: 'T+2 提醒', caption: '核銷兩日後，系統自動 WhatsApp 提醒出分享帖——溫和一句，唔催唔迫。', phoneLabel: '自動跟進', phone: (
      <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
        <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
        <div className="flex-1 flex flex-col justify-end gap-2 pb-4">
          <div className="demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]">
            📦 谷友，禮品到手未呀？🌶️<br /><br />希望食得合口味！食落覺得正嘅話——<br />📸 影張相＋一句真實感想<br />🏷️ Tag #香港辣谷 出個帖<br /><br />你嘅一個分享，係對本地品牌最實際嘅支持 🙏
          </div>
        </div>
      </div>
    ) },
    { icon: '🔗', node: '回貼連結', caption: '客人出分享帖，連結 WhatsApp 傳返——系統自動捕捉。', phoneLabel: '客人回貼', phone: <VerifyScreen /> },
    { icon: '🙏', node: '自動確認', caption: '連結一到，系統秒回「已記錄，核實後谷友福利就完成」——客人即時安心。', phoneLabel: '系統自動', phone: (
      <div className="flex h-full flex-col bg-[#0b141a] px-3 pt-8">
        <div className="mb-2 text-center text-[9px] text-gray-500">WhatsApp</div>
        <div className="flex-1 flex flex-col justify-end gap-2 pb-4">
          <div className="demo-pop rounded-2xl rounded-tr-sm bg-[#005c4b] px-3 py-2.5 text-[10px] text-white ml-auto max-w-[85%]">
            https://www.facebook.com/share/p/19by9HmFHc/
          </div>
          <div className="demo-pop rounded-2xl bg-[#202c33] px-3 py-2.5 text-[10px] leading-relaxed text-gray-200 max-w-[88%]" style={{ animationDelay: '.6s' }}>
            🙏 收到你嘅分享連結，陳大文！已記錄，核實後你嘅谷友福利就完成 ✅
          </div>
        </div>
      </div>
    ) },
    { icon: '✅', node: '核實', caption: '一按核實——分店級導流數據自動累積，導流價值有數為證。', phoneLabel: '數據閉環', phone: <AdminCard action="✅ 分享已核實" label="任務跟蹤 · 到店模式" /> },
  ];
}
